import React from 'react';
import Lottie from 'react-lottie';
import PropTypes from 'prop-types';
import animationData from '../../../assets/animations/animation.json';
import { SafePureComponent } from '../../../utils/SafePureComponent.js';

export const animationIds = {
  officeSpace: 'officeSpace',
  coworking: 'coworking',
  meetingRooms: 'meetingRooms',
  virtualOffice: 'virtualOffice',
  membership: 'membership',
  smallAmount: 'smallAmount',
  mediumAmount: 'mediumAmount',
  bigAmount: 'bigAmount',
};

const animations = {
  [animationIds.officeSpace]: {
    in: [0, 237],
    out: [332, 419],
  },
  [animationIds.coworking]: {
    in: [420, 657],
    out: [752, 839],
  },
  [animationIds.meetingRooms]: {
    in: [840, 1077],
    out: [1172, 1259],
  },
  [animationIds.virtualOffice]: {
    in: [1260, 1497],
    out: [1592, 1679],
  },
  [animationIds.membership]: {
    in: [1680, 1917],
    out: [2012, 2099],
  },
  [animationIds.smallAmount]: {
    in: [2100, 2337],
    out: [2432, 2519],
  },
  [animationIds.mediumAmount]: {
    in: [2520, 2757],
    out: [2852, 2939],
  },
  [animationIds.bigAmount]: {
    in: [2940, 3177],
    out: [3272, 3359],
  },
};

const topicsAnimationsList = [
  animationIds.officeSpace,
  animationIds.coworking,
  animationIds.virtualOffice,
  animationIds.membership,
  animationIds.meetingRooms,
];

const amountAnimationsList = [
  animationIds.smallAmount,
  animationIds.mediumAmount,
  animationIds.bigAmount,
];

export const getTopicsAnimationIdFromIndex = index => {
  return topicsAnimationsList[index] || null;
};

export const getAmountAnimationIdFromIndex = index => {
  return amountAnimationsList[index] || null;
};

/**
 * Reproduces the end of the last animation and the beggining of the next one.
 * Used for transitions. The current animation should be stored in the component.
 */
export const changeAnimation = (currentAnimationId, nextAnimationId) => {
  const currentAnimation = animations[currentAnimationId];
  const nextAnimation = animations[nextAnimationId];
  const currentAnimationOut = currentAnimation && currentAnimation.out;
  const nextAnimationIn = nextAnimation && nextAnimation.in;

  return [currentAnimationOut, nextAnimationIn].filter(value => Boolean(value));
};

export const executeAnimationTransition = (
  animationInstance,
  currentAnimationId,
  nextAnimationId,
) => {
  if (!nextAnimationId || !animationInstance) {
    return null;
  }

  const animationFragments = changeAnimation(
    currentAnimationId,
    nextAnimationId,
  );

  animationInstance.playSegments(animationFragments, true);
};

const defaultPreserveAspectRatio = 'xMidYMax meet';

class LottieAnimation extends SafePureComponent {
  animationRef = React.createRef();
  currentAnimationId = null;

  componentDidMount() {
    const { nextAnimationId } = this.props;

    if (nextAnimationId) {
      this.runAnimation(nextAnimationId);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.nextAnimationId !== this.props.nextAnimationId) {
      this.runAnimation(this.props.nextAnimationId);
    }
  }

  runAnimation = nextAnimationId => {
    const { anim } = this.animationRef.current;
    executeAnimationTransition(anim, this.currentAnimationId, nextAnimationId);
    this.currentAnimationId = nextAnimationId;
  };

  safeRender() {
    const { height, preserveAspectRatio } = this.props;
    const defaultOptions = {
      loop: false,
      autoplay: false,
      animationData: animationData,
      rendererSettings: {
        preserveAspectRatio: preserveAspectRatio || defaultPreserveAspectRatio,
      },
    };

    return (
      <Lottie
        ref={this.animationRef}
        options={defaultOptions}
        isClickToPauseDisabled
        height={height}
      />
    );
  }
}

LottieAnimation.displayName = 'LottieAnimation';

LottieAnimation.propTypes = {
  nextAnimationId: PropTypes.string,
  height: PropTypes.number,
  preserveAspectRatio: PropTypes.string,
};

export default LottieAnimation;
